import React, { Dispatch, useCallback } from 'react'

import { useConversationId } from 'conversations/hooks/useConversationId'
import { getEmbedThemeValue } from 'conversations/modules/embed/selectors'
import { useDropzone } from 'react-dropzone'
import { useDispatch, useSelector } from 'react-redux'
import { uploadFileAsync } from 'shared/store/actions/conversation'

import { Colorway } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { RootAction } from 'typesafe-actions'

import styles from './styles.module.css'

interface DropzoneContextInterface {
  openDropzone: () => void
  onDrop: (files: File[]) => void
}

export const DropzoneContext = React.createContext<DropzoneContextInterface>({
  openDropzone: () => '',
  onDrop: () => '',
})

interface Props {
  children: React.ReactNode
  className?: string
}

const DragOverlay: React.FC = () => (
  <div className={styles.dragOverlay} data-ocweb-id={'DragOverlay'}>
    <Colorway type="3" size="large">
      <Icon name="cw-image" height={120} width={120} />
    </Colorway>
    <h2 className={styles.attachmentHeaderText}>Add attachment to message</h2>
    <p>Drop file to add it to your message</p>
    <p>(25 MB or less)</p>
  </div>
)

export const FileUploadDropzone: React.FC<Props> = ({ children, className }) => {
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const allowAgentFileUpload = useSelector(getEmbedThemeValue('allowAgentFileUpload'))
  const conversationId = useConversationId()
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!conversationId || !allowAgentFileUpload) {
        return
      }

      const MAX_FILES = 10
      const MAX_FILE_SIZE = 25 * 1000000
      const ms = new Date().getTime()
      acceptedFiles.slice(0, MAX_FILES).forEach((file, index) => {
        if (file.size > MAX_FILE_SIZE) {
          dispatch(
            uploadFileAsync.failure({
              conversationId,
              file,
              uploadId: ms + index,
              error: new Error(),
            })
          )
        } else {
          dispatch(
            uploadFileAsync.request({
              conversationId,
              file,
              uploadId: ms + index,
            })
          )
        }
      })
    },
    [allowAgentFileUpload, conversationId, dispatch]
  )
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    disabled: !conversationId,
    onDrop,
  })
  const openDropzone = useCallback(open, [open])
  const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), [])

  return (
    <div
      {...getRootProps({
        multiple: true,
      })}
      className={className}
      data-ocweb-id="FileUploadDropzone">
      {isDragActive && allowAgentFileUpload && <DragOverlay />}
      <DropzoneContext.Provider
        value={{
          openDropzone,
          onDrop,
        }}>
        <input data-ocweb-id="FileUploadInput" {...getInputProps()} onClick={stopPropagation} />
        {children}
      </DropzoneContext.Provider>
    </div>
  )
}
